"use client";

import type { VisualCmsSection } from "@/types/visual-cms";
import { SECTION_CONTAINER, sectionShellStyle } from "./section-shell";

interface HeroSectionProps {
  section: VisualCmsSection;
}

function readString(c: Record<string, unknown>, key: string): string {
  const v = c[key];
  return typeof v === "string" ? v : "";
}

export function HeroSection({ section }: HeroSectionProps) {
  const content = (section.content as Record<string, unknown>) ?? {};
  const title = readString(content, "title");
  const subtitle = readString(content, "subtitle");
  const description = readString(content, "description");
  const imageUrl = readString(content, "imageUrl");
  const imageAlt = readString(content, "imageAlt");
  const ctaText = readString(content, "ctaText");
  const ctaLink = readString(content, "ctaLink");
  const secondaryCtaText = readString(content, "secondaryCtaText");
  const secondaryCtaLink = readString(content, "secondaryCtaLink");

  const layoutRight = (section.layout_variant ?? "").toLowerCase() === "right";
  const shell = sectionShellStyle(section);

  if (!title && !subtitle && !description && !imageUrl) {
    return (
      <section className={shell.className} style={shell.style}>
        <div className={SECTION_CONTAINER}>
          <div className="rounded-lg border border-dashed border-gray-300 px-6 py-12 text-center text-sm text-gray-500">
            Hero section — add a title, image, or CTA to see the design.
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={shell.className} style={shell.style}>
      <div className={SECTION_CONTAINER}>
        <div
          className={`grid grid-cols-1 ${imageUrl ? "lg:grid-cols-2" : ""} gap-10 lg:gap-16 items-center`}
        >
          <div className={layoutRight ? "lg:order-2" : ""}>
            {subtitle && (
              <p className="text-sm font-semibold uppercase tracking-wider text-green-700 mb-3">
                {subtitle}
              </p>
            )}
            {title && (
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-current">
                {title}
              </h1>
            )}
            {description && (
              <p className="mt-5 text-base sm:text-lg text-current/80 max-w-xl">{description}</p>
            )}
            {(ctaText || secondaryCtaText) && (
              <div className="mt-8 flex flex-wrap items-center gap-3">
                {ctaText && (
                  <a
                    href={ctaLink || "#"}
                    className="inline-flex items-center justify-center rounded-md bg-green-600 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-green-700"
                  >
                    {ctaText}
                  </a>
                )}
                {secondaryCtaText && (
                  <a
                    href={secondaryCtaLink || "#"}
                    className="inline-flex items-center justify-center rounded-md border border-current/30 px-5 py-3 text-sm font-semibold hover:bg-current/5"
                  >
                    {secondaryCtaText}
                  </a>
                )}
              </div>
            )}
          </div>

          {imageUrl && (
            <div className={`${layoutRight ? "lg:order-1" : ""}`}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={imageUrl}
                alt={imageAlt || title || ""}
                className="w-full h-auto rounded-xl shadow-md object-cover aspect-[4/3]"
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
